'use client';

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Id } from '../../../convex/_generated/dataModel';

interface User {
  _id: Id<"users">;
  email: string;
  name?: string;
  role: string;
  api_call_limit?: number;
  conversion_limit?: number;
  api_call_credits?: number;
  conversion_credits?: number;
}

interface UserContextType {
  user: User | null;
  setUser: (user: User | null) => void;
  isLoading: boolean;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export const UserProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in from localStorage
    const storedUser = localStorage.getItem('digipin_user');
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (error) {
        console.error('Failed to parse stored user:', error);
        localStorage.removeItem('digipin_user');
      }
    }
    setIsLoading(false);
  }, []);

  const updateUser = (newUser: User | null) => {
    console.log('UserContext: Updating user', newUser ? 'with data' : 'to null');
    setUser(newUser);
    if (newUser) {
      localStorage.setItem('digipin_user', JSON.stringify(newUser));
      console.log('UserContext: Saved user to localStorage');
    } else {
      localStorage.removeItem('digipin_user');
      console.log('UserContext: Removed user from localStorage');
    }
  };

  return (
    <UserContext.Provider value={{ user, setUser: updateUser, isLoading }}>
      {children}
    </UserContext.Provider>
  );
};
